import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from './PageHeader';
import { API } from '../apiClient';

async function api(path, options = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    ...(options.body && !(options.body instanceof FormData) ? {'Content-Type': 'application/json'} : {}),
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const res = await fetch(`${API}${path}`, { ...options, headers });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || (await res.text()) || 'API error');
  return res.json();
}

function formatDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('pl-PL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(value).replace('T', ' ').slice(0, 16);
  }
}


function formatLag(seconds) {
  if (seconds === null || seconds === undefined || seconds === '') return '—';
  const total = Math.max(0, Number(seconds) || 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h) return `${h} h ${m} min ${s} s`;
  if (m) return `${m} min ${s} s`;
  return `${s} s`;
}

function toneClass(tone) {
  if (tone === 'success') return 'success';
  if (tone === 'danger') return 'danger';
  if (tone === 'warning') return 'warning';
  return 'info';
}

function DotTool({ tool }) {
  return (
    <div className="health-tool-row" title={tool.note || tool.label}>
      <span className={`health-dot ${tool.ok ? 'ok' : 'bad'}`} />
      <div>
        <strong>{tool.label}</strong>
        {tool.note ? <small>{tool.note}</small> : null}
      </div>
    </div>
  );
}

function HealthCard({ item, onRunNow, running }) {
  return (
    <article className={`card health-card ${toneClass(item.tone)}`}>
      <div className="health-card-head">
        <div>
          <div className="health-card-title-row">
            <span className={`status-chip ${toneClass(item.tone)}`}>{item.label}</span>
            <span className="health-score">{item.tool_ok_count}/{item.tool_total}</span>
          </div>
          <h2>{item.summary}</h2>
        </div>
        {item.action_key ? (
          <button type="button" className="secondary integration-run-button" disabled={running === item.action_key} onClick={() => onRunNow(item.action_key)}>
            {running === item.action_key ? 'Uruchamiam…' : 'Sprawdź teraz'}
          </button>
        ) : null}
      </div>
      {item.meta?.length ? (
        <div className="health-meta-grid">
          {item.meta.map((meta) => (
            <div className="health-meta-pill" key={`${item.key}-${meta.label}`}>
              <span>{meta.label}</span>
              <strong>{meta.value || '—'}</strong>
            </div>
          ))}
        </div>
      ) : null}
      <div className="health-tool-list">
        {(item.tools || []).map((tool) => <DotTool key={`${item.key}-${tool.key}`} tool={tool} />)}
      </div>
    </article>
  );
}

export default function Health() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [running, setRunning] = useState('');

  async function load() {
    setError('');
    const payload = await api('/portal-health');
    setData(payload);
  }

  async function runNow(key) {
    setRunning(key);
    setError('');
    try {
      await api(`/integrations/run-now/${key}`, { method: 'POST' });
      await load();
    } catch (ex) {
      setError(ex.message || 'Nie udało się uruchomić workera.');
    } finally {
      setRunning('');
    }
  }

  useEffect(() => {
    let mounted = true;
    load().catch((ex) => { if (mounted) setError(ex.message || 'Nie udało się pobrać stanu health.'); });
    return () => { mounted = false; };
  }, []);

  const overview = data?.overview || {};
  const queuesTotal = useMemo(() => (data?.queues || []).reduce((sum, item) => sum + Number(item.count || 0), 0), [data]);

  return (
    <section>
      <PageHeader
        icon="🩺"
        title="Health"
        description="Pełny cockpit stanu portalu: usługi, workery, connectorzy, kolejki, alarmy i ostatnie sygnały z systemu."
        helpKey="health"
        stats={[
          { label: 'Status portalu', value: overview.label || 'Ładowanie…', tone: toneClass(overview.tone) },
          { label: 'Zielone punkty', value: overview.total_tools ? `${overview.ok_tools}/${overview.total_tools}` : '—', tone: toneClass(overview.tone) },
          { label: 'Workery', value: overview.worker_count ?? '—', tone: 'info' },
          { label: 'Alarmy', value: overview.alert_count ?? '—', tone: (overview.alert_count || 0) > 0 ? 'danger' : 'success' },
          { label: 'Kolejki razem', value: queuesTotal, tone: queuesTotal > 0 ? 'warning' : 'success' },
        ]}
      />

      {error ? <div className="error dashboard-error">{error}</div> : null}

      <section className="dashboard-grid dashboard-grid-optimized health-overview-grid">
        <article className={`card status-card hero-status ${toneClass(overview.tone)}`}>
          <div className="status-card-head">
            <span className={`status-chip ${toneClass(overview.tone)}`}>Sygnał ogólny</span>
          </div>
          <h2>{overview.label || 'Ładowanie stanu portalu...'}</h2>
          <p className="muted">Ostatnie wygenerowanie: {formatDateTime(data?.generated_at)}</p>
          <div className="health-facts-list">
            {(data?.facts || []).map((fact) => (
              <div className="channel-row" key={fact.label}><span>{fact.label}</span><strong>{fact.value}</strong></div>
            ))}
          </div>
        </article>

        <article className="card status-card info">
          <div className="status-card-head">
            <span className="status-chip info">Alarmy i rzeczy do reakcji</span>
          </div>
          <h2>{(data?.alerts || []).length ? 'Są aktywne sygnały' : 'Brak aktywnych alarmów'}</h2>
          <div className="health-alert-list">
            {(data?.alerts || []).length ? data.alerts.map((alert, idx) => (
              <div className={`health-alert ${alert.level === 'danger' ? 'danger' : 'warning'}`} key={`${alert.integration}-${idx}`}>
                <strong>{alert.integration}</strong>
                <span>{alert.message}</span>
              </div>
            )) : <p className="muted">Żaden worker ani kolejka nie zgłaszają teraz krytycznego alertu.</p>}
          </div>
        </article>

        <article className="card status-card info">
          <div className="status-card-head">
            <span className="status-chip info">Ostatnie sygnały z portalu</span>
          </div>
          <h2>Co wydarzyło się ostatnio</h2>
          <div className="health-activity-list">
            {(data?.recent_activity || []).map((item) => (
              <div className="health-activity-row" key={`${item.label}-${item.at}`}>
                <div>
                  <strong>{item.label}</strong>
                  <small>{item.summary}</small>
                </div>
                <time>{formatDateTime(item.at)}</time>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="health-queue-grid">
        <article className="card health-queue-card info">
          <div className="health-queue-top">
            <span className="status-chip info">Import rezerwacji / 30 dni</span>
            <strong>{data?.import_latency?.sample_count ?? 0}</strong>
          </div>
          <p>
            Mediana: <strong>{data?.import_latency?.sample_count ? formatLag(data.import_latency.median_seconds) : 'brak automatycznych importów'}</strong>
            {' '}· max: <strong>{data?.import_latency?.sample_count ? formatLag(data.import_latency.max_seconds) : 'brak automatycznych importów'}</strong>
          </p>
          <p className="muted">Liczba importów per ścieżka (30 dni): {(data?.import_latency?.path_counts || []).map((row) => `${row.path}: ${row.count}`).join(', ') || 'brak danych'}</p>
        </article>
      </section>

      <div className="health-section-head">
        <h2>Usługi bazowe</h2>
        <p className="muted">To, bez czego portal nie ma szans działać poprawnie.</p>
      </div>
      <section className="health-card-grid">
        {(data?.services || []).map((item) => <HealthCard key={item.key} item={item} onRunNow={runNow} running={running} />)}
      </section>

      <div className="health-section-head">
        <h2>Workery i connectorzy</h2>
        <p className="muted">Każda mała kropka oznacza konkretny warunek: narzędzie, zależność lub zadanie danego workera.</p>
      </div>
      <section className="health-card-grid">
        {(data?.workers || []).map((item) => <HealthCard key={item.key} item={item} onRunNow={runNow} running={running} />)}
      </section>

      <div className="health-section-head">
        <h2>Kolejki i ciśnienie operacyjne</h2>
        <p className="muted">To nie są tylko liczniki. Każda pozycja mówi, gdzie portal zaczyna się zapychać albo gubić reakcję.</p>
      </div>
      <section className="health-queue-grid">
        {(data?.queues || []).map((item) => (
          <article className={`card health-queue-card ${toneClass(item.tone)}`} key={item.key}>
            <div className="health-queue-top">
              <span className={`status-chip ${toneClass(item.tone)}`}>{item.label}</span>
              <strong>{item.count}</strong>
            </div>
            <p>{item.note}</p>
          </article>
        ))}
      </section>
    </section>
  );
}
